import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Clock, Trophy, Users, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { RichText } from "../components/RichText";

const LOGO =
  "https://customer-assets.emergentagent.com/job_5d53cc0c-f8e0-4354-807b-347bf58e1216/artifacts/3rlolaxv_Logo.png";

const VALUE_ICONS = [Shield, Clock, Trophy, Users];

export default function About() {
  const [c, setC] = useState(null);
  useEffect(() => {
    api.get("/site-content").then((r) => setC(r.data)).catch(() => {});
  }, []);

  if (!c)
    return (
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-20 text-neutral-400" data-testid="about-loading">
        Loading…
      </div>
    );

  return (
    <div data-testid="about-page" className="text-white">
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24">
        <div className="text-xs uppercase tracking-[0.35em] text-axe-tangerine font-bold">
          {c.about_eyebrow || "Our Mission"}
        </div>
        <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.03]">
          {(c.about_headline || "Forge Your Legacy Among AXE").split(" ").map((w, i, arr) =>
            i === arr.length - 2 ? (
              <span key={i} className="text-axe-purple glow-purple">{w} </span>
            ) : (
              <span key={i}>{w} </span>
            )
          )}
        </h1>

        <div className="mt-14 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7 space-y-6 text-neutral-300 text-base sm:text-lg leading-relaxed">
            {c.about_mission && (
              <div>
                <span className="text-white font-semibold">The Mission. </span>
                <RichText text={c.about_mission} className="mt-2" />
              </div>
            )}
            {c.about_why && (
              <div>
                <span className="text-white font-semibold">Why 'AXE'? </span>
                <RichText text={c.about_why} className="mt-2" />
              </div>
            )}

            {c.about_stats?.length > 0 && (
              <div className="grid sm:grid-cols-3 gap-4 pt-4">
                {c.about_stats.map((s) => (
                  <div key={s.key} className="rounded-2xl border border-axe-border bg-axe-surface p-5">
                    <div className="text-[11px] uppercase tracking-[0.3em] text-axe-purple font-bold">
                      {s.key}
                    </div>
                    <div className="mt-2 font-display font-black text-2xl text-white">{s.value}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="relative overflow-hidden rounded-3xl border border-axe-border bg-axe-surface aspect-[4/5]">
              <img
                src={c.about_image || "https://images.unsplash.com/photo-1758410473619-c204b1bcf13a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"}
                alt="AXE team"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-axe-surface via-axe-surface/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 flex items-center gap-4">
                <img src={LOGO} alt="" className="h-14 w-14 glow-purple" />
                <div>
                  <div className="text-[11px] uppercase tracking-[0.3em] text-axe-tangerine font-bold">
                    Team Victory
                  </div>
                  <div className="mt-1 font-display font-bold text-lg">Anti Xquad'E</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {c.about_values?.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 md:px-8 mt-24">
          <div className="mb-10">
            <div className="text-xs uppercase tracking-[0.35em] text-axe-tangerine font-bold">
              Core Values
            </div>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl">What We Stand For</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {c.about_values.map((v, i) => {
              const Icon = VALUE_ICONS[i % VALUE_ICONS.length];
              return (
                <div
                  key={v.title}
                  data-testid={`about-value-${v.title.toLowerCase()}`}
                  className="rounded-2xl border border-axe-border bg-axe-surface p-7 hover:border-axe-purple/60 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-axe-purple/15 border border-axe-purple/40 grid place-items-center">
                      <Icon className="text-axe-purple" size={22} />
                    </div>
                    <h3 className="font-display font-bold text-2xl text-white">{v.title}</h3>
                  </div>
                  <p className="mt-5 text-neutral-300 leading-relaxed">{v.text}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-5 md:px-8 mt-24">
        <div className="relative overflow-hidden rounded-3xl border border-axe-border bg-axe-surface p-10 md:p-14 text-center">
          <div className="text-xs uppercase tracking-[0.35em] text-axe-tangerine font-bold">
            The Next Step
          </div>
          <h3 className="mt-3 font-display font-black text-3xl sm:text-4xl">
            Ready to sharpen your skills?
          </h3>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/recruitment" className="inline-flex items-center gap-2 rounded-full bg-axe-purple px-7 py-3.5 font-bold uppercase tracking-[0.22em] text-white hover:bg-axe-purple-hover transition-colors drop-shadow-[0_0_14px_rgba(176,38,255,0.45)]" data-testid="about-cta-apply">
              Apply to Join the Roster <ArrowRight size={16} />
            </Link>
            <a href="https://discord.gg/antixquade" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-axe-tangerine px-7 py-3.5 font-bold uppercase tracking-[0.22em] text-black hover:bg-axe-tangerine-hover transition-colors" data-testid="about-cta-discord">
              Join Our Discord
            </a>
            <Link to="/tournaments" className="inline-flex items-center gap-2 rounded-full border-2 border-axe-purple px-7 py-3 font-bold uppercase tracking-[0.22em] text-axe-purple hover:bg-axe-purple hover:text-white transition-all" data-testid="about-cta-news">
              Tournament Highlights
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
